import { useEffect, useRef, useState } from "react";
import { apiFetch, useApp } from "../../context/AppContext";
import { Button, Modal } from "./UI";
import LoadProgress from "./LoadProgress";
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Loader2,
  Upload,
} from "lucide-react";

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PolicyUploadModal({
  open,
  onClose,
  onSuccess,
  policy,
}) {
  const { showNotification } = useApp();
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [actRef, setActRef] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const isEdit = Boolean(policy && policy.id);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setTitle("");
      setActRef("");
      setDescription("");
      setError("");
      setDragOver(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && policy) {
      setTitle(policy.title || "");
      setActRef(policy.act_ref || "");
      setDescription(policy.description || "");
      setError("");
      setFile(null);
    }
  }, [open, policy]);

  const isAllowedFile = (name) => {
    const lower = (name || "").toLowerCase();
    if (lower.endsWith(".pdf")) return true;
    return isEdit && lower.endsWith(".docx");
  };

  const handleFile = (nextFile) => {
    if (!nextFile) return;
    if (!isAllowedFile(nextFile.name)) {
      setError(
        isEdit
          ? "Please upload a PDF or Word (.docx) file"
          : "Please upload a PDF file",
      );
      return;
    }
    setError("");
    setFile(nextFile);
    if (!title.trim()) {
      const base = nextFile.name
        .replace(/\.(pdf|docx)$/i, "")
        .replace(/[_-]+/g, " ");
      setTitle(base);
    }
  };

  const handleUpload = async () => {
    if (!file && !isEdit) {
      setError("Select a PDF to upload");
      return;
    }
    if (isEdit && !file) {
      setError("Select the edited PDF or Word file to replace the policy");
      return;
    }
    if (!title.trim()) {
      setError("Policy title is required");
      return;
    }

    setUploading(true);
    setError("");
    try {
      const form = new FormData();
      form.append("title", title.trim());
      form.append("file", file);
      form.append("act_ref", actRef.trim());
      form.append("description", description.trim());

      const url = isEdit
        ? `/policy-store/${policy.id}/replace`
        : "/policy-store";

      const res = await apiFetch(url, { method: "POST", body: form, cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(
          data.detail || (isEdit ? "Replace failed" : "Upload failed"),
        );

      showNotification(
        isEdit ? "Policy file replaced" : "Policy uploaded successfully",
        "success",
      );
      onSuccess?.(data);
      onClose?.();
    } catch (err) {
      setError(err.message || (isEdit ? "Replace failed" : "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Replace policy file" : "Upload Policy"}
      size="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          {isEdit
            ? "Upload the edited PDF, or the Word document downloaded via “Edit as Word”. The stored policy preview will update to this file."
            : "Add an approved DPDP policy document to your organisation's central policy repository."}
        </p>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files[0]);
          }}
          onClick={() => fileRef.current?.click()}
          className={`cursor-pointer rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-8 gap-2.5 transition-all ${
            dragOver
              ? "border-indigo-400 bg-indigo-50"
              : "border-slate-200 hover:border-slate-400"
          }`}
        >
          <div
            className={`p-3 rounded-xl ${dragOver ? "bg-indigo-100" : "bg-slate-100"}`}
          >
            {file ? (
              <FileText size={22} className="text-indigo-600" />
            ) : (
              <Upload
                size={22}
                className={dragOver ? "text-indigo-500" : "text-slate-400"}
              />
            )}
          </div>
          <div className="text-center px-4">
            <p className="text-sm font-semibold text-slate-700">
              {file
                ? file.name
                : isEdit
                  ? "Drop the edited policy file here"
                  : "Drop your policy PDF here"}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {file
                ? formatBytes(file.size)
                : isEdit
                  ? "or click to browse — PDF or Word (.docx), max 50 MB"
                  : "or click to browse — PDF only, max 50 MB"}
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept={
              isEdit
                ? ".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                : ".pdf,application/pdf"
            }
            className="hidden"
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Policy title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Personal Data Protection Policy"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              DPDP reference (optional)
            </label>
            <input
              type="text"
              value={actRef}
              onChange={(e) => setActRef(e.target.value)}
              placeholder="e.g. DPDP Act §8"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Brief summary or version notes"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 resize-none"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
            <AlertTriangle size={14} className="flex-shrink-0" />
            {error}
          </div>
        )}

        {file && title.trim() && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm">
            <CheckCircle2 size={14} className="flex-shrink-0" />
            Ready to {isEdit ? "replace" : "upload"} &ldquo;{title.trim()}&rdquo;
          </div>
        )}

        <LoadProgress
          active={uploading}
          label={isEdit ? "Replacing file" : "Uploading"}
          durationMs={18000}
        />

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button variant="secondary" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={uploading || !file || !title.trim()}
            className="flex items-center gap-1.5"
          >
            {uploading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Upload size={14} />
            )}
            {isEdit ? "Replace file" : "Upload policy"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
